<template>
    <pendo-app
        class="vue-wrapper"
        :class="{
            'side-panel-open': panelOpen,
            'vue-wrapper--new-ui': newUiShellActive
        }">
        <topbar v-if="isOnGlobalSettings" />
        <pendo-page
            ref="pendoPage"
            :class="[noFooterRowClass]"
            background-color="var(--palette-gray-5)">
            <keep-alive :include="keepaliveInclude">
                <router-view-wrapper ref="routerViewWrapper">
                    <template v-if="!isOnTrialExpiredRoute">
                        <install-snippet-banner v-if="showInstallSnippetBanner" />
                        <sample-data-banner v-if="authenticated" />
                    </template>
                    <router-view :key="routerViewKey" />
                </router-view-wrapper>
            </keep-alive>
        </pendo-page>
        <transition name="slide-panel">
            <side-panel-wrapper
                :is-open="panelOpen"
                :render-content="panelContent"
                class="side-panel-wrapper" />
        </transition>
    </pendo-app>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { canHaveGlobalBanner } from '@/global-banner/global-banner.utils';
import InstallSnippetBanner from '../free/install-snippet-banner/install-snippet-banner.vue';
import SampleDataBanner from '../free/sample-data-banner/sample-data-banner.vue';
import { PendoApp, PendoPage } from '@pendo/components-vue3';
import SidePanelWrapper from './side-panel-wrapper/side-panel-wrapper.vue';
import { EventBus } from '@/utils/event-bus.js';
import { isOnGlobalSettingsPage, isOnReplayPlayerPage, isOnListenPage, isOnDataSyncPage, shouldShowNavItems } from '@/vue-router/route.utils';
import Topbar from '@/admin/topbar/topbar.vue';
import { useResizeObserver } from '@vueuse/core';
import { useTemplateRef, onMounted, watch } from 'vue';
import { useFixedStyles } from '@/common/use/use-fixed-styles/use-fixed-styles';
import useUiVersion from '@/common/use/use-ui-version/use-ui-version';
import { tabbable } from 'tabbable';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

// router-view expects its parent to have a $createElement method, which is
// only applied to components that have compat mode enabled. @vue/compat
// doesn't enable compat mode for built-in components, but in versions prior to
// v3.5.1, it erroneously enabled compat mode for the keep-alive built-in. To
// work around this, we must wrap router-view in a passthrough component that
// has compat mode enabled.
import RouterViewWrapper from './router-view-wrapper.vue';

export default {
    name: 'VueWrapper',
    components: {
        InstallSnippetBanner,
        SampleDataBanner,
        PendoApp,
        PendoPage,
        RouterViewWrapper,
        SidePanelWrapper,
        Topbar,
    },
    emits: ['unmount'],
    setup () {
        const pendoPage = useTemplateRef('pendoPage');
        const { drawerStyles, affixStyles, setPendoPageRightMargin } = useFixedStyles();
        const { isNewUi } = useUiVersion();

        watch(
            () => drawerStyles.value,
            ({ top, right, bottom, pushDrawerShellChrome, pushDrawerShellBorderRadiusPx }) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--drawer-top-position', `${top}px`);
                root.style.setProperty('--drawer-right-position', `${right}px`);
                root.style.setProperty('--drawer-bottom-position', `${bottom}px`);
                root.style.setProperty(
                    '--new-ui-push-drawer-border-bottom',
                    pushDrawerShellChrome ? '1px solid var(--palette-gray-40)' : 'none',
                );
                root.style.setProperty(
                    '--new-ui-push-drawer-border-bottom-right-radius',
                    pushDrawerShellChrome ? `${pushDrawerShellBorderRadiusPx}px` : '0px',
                );
            },
            { immediate: true },
        );

        watch(
            () => affixStyles.value.fixedStyles.top,
            (top) => {
                const root = document.querySelector(':root');
                root.style.setProperty('--top-nav-height', `${top}px`);
            },
            { immediate: true },
        );

        onMounted(() => {
            useResizeObserver(pendoPage, (entries) => {
                const entry = entries[0];
                const { width: pendoPageWidth } = entry.contentRect;
                setPendoPageRightMargin(pendoPageWidth);
            });
        });

        return {
            isNewUi,
            pendoPage,
        };
    },
    data () {
        return {
            panelOpen: false,
            panelContent: { __render: () => {} },
            keepaliveInclude: ['Retention', 'Groups'],
        };
    },
    computed: {
        ...mapState({
            authenticated: (state) => state.auth.authenticated,
            isTopBannerDismissed: (state) => state.leftNav.isTopBannerDismissed,
        }),
        isOnGlobalSettings () {
            return isOnGlobalSettingsPage(this.$route);
        },
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        canHaveGlobalBanner () {
            const params = {
                isTopBannerDismissed: this.isTopBannerDismissed,
                currentRoute: this.$route?.name,
            };

            return canHaveGlobalBanner(params);
        },
        routerViewKey () {
            // This is a workaround to prevent the router-view from remounting when navigating
            // between different pages in the Listen module
            if (isOnListenPage(this.$route)) {
                return 'pendo-listen';
            }
            // This is a workaround to prevent the router-view from remounting when navigating
            // between replays in the queue
            if (isOnReplayPlayerPage(this.$route.path)) {
                return 'replay-player';
            }
            // This is a workaround to prevent the router-view from remounting when updating
            // the URL based on progress through the data-sync setup flow
            if (isOnDataSyncPage(this.$route.path)) {
                return 'data-sync';
            }

            return this.$route?.path || 'pendo-app';
        },
        showInstallSnippetBanner () {
            return !this.$productLine(PRODUCT_LINES.ADOPT);
        },
        newUiShellActive () {
            return this.isNewUi && shouldShowNavItems(this.$route, this.authenticated);
        },
    },
    watch: {
        '$route.path': {
            handler () {
                setTimeout(() => {
                    const routerViewWrapper = this.$refs.routerViewWrapper?.$el || this.$refs.routerViewWrapper;

                    if (!routerViewWrapper) {
                        return;
                    }

                    // focus the first heading if it exists
                    const firstHeading = routerViewWrapper.querySelector?.('h1');

                    if (firstHeading) {
                        firstHeading.setAttribute('tabindex', '-1');
                        firstHeading.style.outline = 'none';
                        firstHeading.focus();

                        return;
                    }

                    // focus the first tabbable element if it exists
                    const [firstTabbableElement] = tabbable(routerViewWrapper);

                    if (firstTabbableElement) {
                        firstTabbableElement.focus();

                        return;
                    }

                    // focus the router-view-wrapper if no other element is focusable
                    routerViewWrapper.setAttribute('tabindex', '-1');
                    routerViewWrapper.focus();
                }, 200);
            },
            flush: 'post',
            immediate: true,
        },
    },
    created () {
        this.updateCurrentMauLimit();

        EventBus.$on('openSidePanel', this.onOpenSidePanel);
        EventBus.$on('closeSidePanel', this.onCloseSidePanel);
        EventBus.$on('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$on('closeTopBanner', this.onCloseTopBanner);
    },
    mounted () {
        if (!this.canHaveGlobalBanner) {
            this.setTopBannerHeight({ topBannerHeight: 0 });
        }
    },
    beforeUnmount () {
        EventBus.$off('openSidePanel', this.onOpenSidePanel);
        EventBus.$off('closeSidePanel', this.onCloseSidePanel);
        EventBus.$off('sidePanelClosed', this.onSidePanelClosed);
        EventBus.$off('closeTopBanner', this.onCloseTopBanner);
    },
    methods: {
        ...mapActions({
            updateCurrentMauLimit: 'subscription/updateCurrentMauLimit',
        }),
        ...mapMutations({
            setTopBannerDismissed: 'leftNav/setTopBannerDismissed',
            setTopBannerHeight: 'leftNav/setTopBannerHeight',
        }),
        onOpenSidePanel (renderPanel) {
            const wasAlreadyOpen = this.panelOpen;
            this.panelOpen = true;
            this.panelContent = renderPanel;
            this.$nextTick(() => {
                setTimeout(() => {
                    EventBus.$emit('focusSidePanel');
                }, wasAlreadyOpen ? 50 : 400);
            });
        },
        onCloseSidePanel () {
            this.panelOpen = false;
        },
        onSidePanelClosed () {
            this.panelOpen = false;
        },
        onCloseTopBanner () {
            this.setTopBannerDismissed({ isTopBannerDismissed: true });
        },
    },
};
</script>

<style lang="less" scoped>
.vue-wrapper {
    display: flex !important;
    flex-direction: column;
    flex-grow: 1;
    position: relative !important;
    inset: unset !important;

    @panel-width: 377px;
    .slide-panel-enter-active,
    .slide-panel-leave-active {
        transition: transform 0.3s;
    }

    .slide-panel-enter-to,
    .slide-panel-leave {
        transform: translateX(0);
    }

    .slide-panel-enter,
    .slide-panel-leave-to {
        transform: translateX(@panel-width);
    }

    .pendo-page {
        transition: margin-right 220ms;
        position: relative !important;
    }

    .side-panel-wrapper {
        z-index: 1;
        background-color: var(--palette-gray-0);
        width: @panel-width;
        position: fixed;
        right: 0;
        top: var(--drawer-top-position);
        bottom: 0;
    }

    &.side-panel-open {
        .pendo-page {
            margin-right: @panel-width;
        }
    }

    &--new-ui {
        overflow: hidden;

        .side-panel-wrapper {
            position: absolute;
            top: 0;
        }
    }
}
</style>
